import React, {
  useState,
  ChangeEvent,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from "react";
import {
  Upload,
  Button,
  Table,
  message,
  Input,
  DatePicker,
  Typography,
  Radio,
  Select,
  Modal,
  Checkbox,
  Alert,
} from "antd";
import type { UploadProps } from "antd/es/upload/interface";
import dayjs, { Dayjs } from "dayjs";
import {
  UploadOutlined,
  CloseOutlined,
  CalendarOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import Papa from "papaparse";
import { GeoLiftJobConfigService } from "../../../../services/jobConfigServiceFactory/GeoLiftJobConfigService";
import {
  summarizeMarkets,
  STATE_NAME_TO_POSTAL,
} from "../../../../utils/utils";
import { fuzzyMatchLocation } from "../../../../utils/locationMatching";
import WorldUSSalesMap from "../../../../components/WorldHoldoutTreatmentMap";
import ColumnMappingModal from "../../../../components/ColumnMappingModal";
import DatabaseDataSelector from "../../../../components/DatabaseDataSelector";
import DataPreviewValidationModal from "../../../../components/DataPreviewValidationModal";
import {
  validateGeoLiftData,
  convertMappingsToBackendFormat,
  applyColumnMapping,
  type ColumnMapping,
  type ValidationResult,
  type GeoLiftDataValidationResponse,
} from "../../../../utils/dataValidation";
import axiosInstance from "../../../../utils/axiosInstance";
import { GEO_LIFT_GET_TEST_MARKET } from "../../../../constants/API";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { useSearchParams, useParams } from "react-router-dom";
import {
  DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD,
  MIN_GEO_LIFT_LOCATIONS,
  MIN_GEO_LIFT_TIME_PERIODS,
} from "../../../../constants/common";
import CustomExcelIcon from "../../../../assets/icons/CustomExcelIcon";
import { downloadGeoLiftSampleTemplate } from "../../../../utils/geoLiftSampleTemplate";

const { Text } = Typography;
export interface JobConfig {
  tenantId: string;
  jobConfigId: string;
  status: string;
  name: string;
  jobType: string;
  is_ai_qc_enabled: boolean;
  description: string | null;
  dataSourceConfigId: string | null;

  dataTransformationConfig: Record<string, unknown>;

  dataMappingConfig: {
    mappings: {
      geoLift: {
        columnMapping: {
          location_variables: string[];
          date_variables: string[];
          kpi_variables: string[];
        };
      };
    };
  };

  modelVersion: string;

  modelParameterConfig: {
    parameters: {
      buyType: string;
      scmType: string;
      regressionType: string;
      isSales: boolean;
      dateFormat: string;
      durationType: string;
      includeMarket: string[];
      campaignStartDate: string;
      bauSpent: number;
      statSigThreshold: number;
    };
  };

  modelOutputConfig: {
    outputData: {
      aggregateLift: string;
      outputWithoutCampaign: number;
      totalAdSpend: number;
      outputWithCampaign: number;
    };
  };

  createdDate: string;
  modifiedDate: string;
  version: number;
}

interface PreCampaignProps {
  jobConfigId: string;
  fileData?: string;
  onValuesChange?: (values: {
    geoLiftStudyName: string;
    buyType: "national" | "non-national";
    fileList: File[];
    columnMappings?: { [key: string]: string | null };
    campaignStartDate?: string;
    is_ai_qc_enabled: boolean;
    bauSpent: number;
    statSigThreshold: number;
    isSales: boolean | undefined;
    // Campaign Start Date falls entirely outside the uploaded data's date
    // range - there's no valid pre/post split possible, so this blocks
    // study creation rather than just warning (see campaignDateWarning
    // below, type "error").
    campaignDateOutOfRange?: boolean;
  }) => void;
  initialValues?: {
    geoLiftStudyName: string;
    buyType: "national" | "non-national";
    fileList: File[];
    columnMappings?: { [key: string]: string | null };
    campaignStartDate?: string;
    is_ai_qc_enabled: boolean;
    bauSpent: number;
    statSigThreshold: number;
    isSales: boolean | undefined;
  };
  setCampaignDate: (value: Dayjs | null) => void;
  setFileUploaded?: React.Dispatch<React.SetStateAction<boolean>>;
  mode: any;
  selectedMarketsIncludes: any[];
  setScmType: (e: string) => void;
  setSelectedMarketsIncludes: (e: string[]) => void;
  scmType: string;
  setRegressionType: (e:string) => void;
  regressionType: string;
  isSales: boolean;
  setIsSales: (e: boolean) => void;
  viewMode?: boolean;
}

export interface PreCampaignRef {
  getData: () => {
    geoLiftStudyName: string;
    buyType: "national" | "non-national";
    fileList: File[];
    campaignStartDate?: string;
    bauSpent: number;
    statSigThreshold: number;
    isSales: boolean;
  };
}

// Y and adspend can come in with long decimals - round them to 2 places for
// the preview table, everywhere it's built.
const DECIMAL_PREVIEW_COLUMNS = new Set(["y", "adspend"]);

const buildPreviewColumns = (headers: string[]) =>
  headers.map((header) => {
    const dataIndex = header.toLowerCase().replace(/\s+/g, "_");
    if (!DECIMAL_PREVIEW_COLUMNS.has(dataIndex)) {
      return { title: header, dataIndex };
    }
    return {
      title: header,
      dataIndex,
      render: (value: any) => {
        const num = Number(value);
        return Number.isFinite(num) ? num.toFixed(2) : value;
      },
    };
  });

const PreCampaign = forwardRef<PreCampaignRef, PreCampaignProps>(
  (
    {
      jobConfigId,
      fileData,
      onValuesChange,
      initialValues,
      setFileUploaded,
      setCampaignDate,
      mode,
      selectedMarketsIncludes,
      setSelectedMarketsIncludes,
      scmType,
      setScmType,
      regressionType="SCM",
      setRegressionType,
      isSales,
      setIsSales,
      viewMode = false,
    },
    ref
  ) => {
    const [searchParams] = useSearchParams();
    const { id: urlId } = useParams<{ id: string }>();
    const edit = searchParams.get("edit");
    const initialName = searchParams.get("name");
    const geoLiftJobConfigService = new GeoLiftJobConfigService();
    const initialValuesRef = useRef(initialValues);
    const isMounted = useRef(false);
    const [geoLiftStudyName, setTestName] = useState(initialName || "");
    const [buyType, setBuyType] = useState<"national" | "non-national">(() => {
      if (initialValues?.buyType) {
        return initialValues.buyType;
      }
      return mode === "evaluate" ? "national" : "non-national";
    });
    const [loading, setLoading] = useState(false);
    const [csvData, setCsvData] = useState<any[]>([]);
    const [columns, setColumns] = useState<
      { title: string; dataIndex: string }[]
    >([]);
    const [fileList, setFileList] = useState<File[]>(
      initialValues?.fileList || []
    );
    const result = summarizeMarkets(csvData);
    const [validationResult, setValidationResult] =
      useState<ValidationResult | null>(null);
    const [showMappingModal, setShowMappingModal] = useState(false);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [validatingData, setValidatingData] = useState(false);
    const [dataValidationResponse, setDataValidationResponse] =
      useState<GeoLiftDataValidationResponse | null>(null);
    const [canonicalFileForUpload, setCanonicalFileForUpload] =
      useState<File | null>(null);
    const [pendingBackendMappings, setPendingBackendMappings] = useState<{
      [key: string]: string | null;
    } | null>(null);
    const [columnMappings, setColumnMappings] = useState<{
      [key: string]: string | null;
    }>({});
    const [campaignStartDate, setCampaignStartDate] = useState<Dayjs | null>(
      null
    );
    const [isAiQcEnabled, setIsAiQcEnabled] = useState(initialValues?.is_ai_qc_enabled || false);
    const getCurrectGeneratedJob = useSelector(
      (state: RootState) => state.geoLift.jobConfigList
    );
    useEffect(() => {
      // Populate data for both view and edit modes when jobConfigId exists
      if (jobConfigId && getCurrectGeneratedJob.length > 0) {
        const date: JobConfig = getCurrectGeneratedJob.find(
          (ele) => ele?.jobConfigId === jobConfigId
        );
        if (date) {
          setCampaignStartDate(
            dayjs(date.modelParameterConfig.parameters.campaignStartDate)
          );
          setSelectedMarketsIncludes(
            date?.modelParameterConfig?.parameters?.includeMarket || []
          );
          setScmType(
            date?.modelParameterConfig?.parameters?.scmType || "single"
          );
          setRegressionType(
            date?.modelParameterConfig?.parameters?.regressionType || "SCM"
          );

          setIsSales(
            date?.modelParameterConfig?.parameters?.isSales ?? true
          )


          setTestName(date?.name || "");

          // Sync Redux value to local state
          setIsAiQcEnabled(!!date.is_ai_qc_enabled);
          setBauSpent(
            date.modelParameterConfig?.parameters?.bauSpent
              ? String(date.modelParameterConfig.parameters.bauSpent)
              : "0"
          );
          setStatSigThreshold(String(date.modelParameterConfig.parameters.statSigThreshold ?? DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD))
        }
      }
    }, [jobConfigId, getCurrectGeneratedJob]);
    const [bauSpent, setBauSpent] = useState<string>("");
    const [dataSource, setDataSource] = useState<"csv" | "database">("csv");
    const [loadingDatabase, setLoadingDatabase] = useState(false);
    const [showDatabaseModal, setShowDatabaseModal] = useState(false);
    const [statSigThreshold, setStatSigThreshold] = useState<string>(`${DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD}`);

    useEffect(() => {
      console.log("initialValues", initialValues)
      if (initialValuesRef.current !== initialValues) {
        setTestName(initialValues?.geoLiftStudyName || "");
        setBuyType(initialValues?.buyType || "non-national");
        setFileList(initialValues?.fileList || []);
        if (initialValues?.isSales !== undefined && initialValues?.isSales !== null) {
          setIsSales(initialValues.isSales);
        }
        initialValuesRef.current = initialValues;
      }
    }, [initialValues]);

    useEffect(() => {
      if (isMounted.current) {
        const modeBuyType = mode === "evaluate" ? "national" : "non-national";
        setBuyType(modeBuyType);
        if (onValuesChange) {
          onValuesChange({
            geoLiftStudyName,
            buyType: modeBuyType,
            fileList,
            columnMappings,
            campaignStartDate: mode
              ? campaignStartDate?.format("YYYY-MM-DD")
              : undefined,
            is_ai_qc_enabled: isAiQcEnabled,
            bauSpent: Number(bauSpent),
            statSigThreshold: Number(statSigThreshold || DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD),
            isSales: isSales,
            campaignDateOutOfRange: campaignDateWarning?.type === "error",
          });
        }
      } else {
        isMounted.current = true;
        if (onValuesChange) {
          onValuesChange({
            geoLiftStudyName,
            buyType,
            fileList,
            columnMappings,
            campaignStartDate:
              campaignStartDate?.format("YYYY-MM-DD") || undefined,
            is_ai_qc_enabled: isAiQcEnabled,
            bauSpent: Number(bauSpent),
            statSigThreshold: Number(statSigThreshold || DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD),
            isSales: isSales,
            campaignDateOutOfRange: campaignDateWarning?.type === "error",
          });
        }
      }
    }, [mode, geoLiftStudyName, fileList, columnMappings, campaignStartDate, isAiQcEnabled, bauSpent, statSigThreshold]);

    useImperativeHandle(ref, () => ({
      getData: () => ({
        geoLiftStudyName,
        buyType,
        fileList,
        campaignStartDate: campaignStartDate?.format("YYYY-MM-DD"),
        bauSpent: Number(bauSpent),
        is_ai_qc_enabled: isAiQcEnabled,
        statSigThreshold: Number(statSigThreshold || DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD),
        isSales: isSales,
      }),
    }));

    const handleDateChange = (date: Dayjs | null) => {
      if (date) {
        const selectedDate = date.endOf("day");
        setCampaignStartDate(selectedDate);
        setCampaignDate(selectedDate);
      } else {
        setCampaignStartDate(null);
        setCampaignDate(null);
      }
    };
    const checkPostTestData = async () => {
      if (!jobConfigId) return;
      // View/edit mode - read cached location data, don't re-geocode live
      // (see loadCachedLocationData's comment for why).
      loadCachedLocationData();
      setLoading(true);

      try {
        // 1️⃣ Fetch CSV as Blob
        const fileResponse = await axiosInstance.get(
          `/api/s3/getFile?path=${jobConfigId}/input/geo_lift_data_full.csv`,
          {
            responseType: "blob",
          }
        );

        // 2️⃣ Create File from Blob (same as upload)
        const csvFile = new File(
          [fileResponse.data],
          "geo_lift_data_full.csv",
          {
            type: "text/csv",
          }
        );

        // 3️⃣ Parse CSV (inline, same logic as upload)
        Papa.parse<string[]>(csvFile, {
          skipEmptyLines: true,
          complete: (result) => {
            const data = result.data;

            if (!data || data.length === 0) {
              message.error("Empty CSV or invalid format.");
              setFileUploaded?.(false);
              return;
            }

            const headers = data[0];

            const generatedColumns = buildPreviewColumns(headers);

            const tableData = data.slice(1).map((row, index) => ({
              key: index,
              ...headers.reduce((acc, header, i) => {
                acc[header.toLowerCase().replace(/\s+/g, "_")] = row[i];
                return acc;
              }, {} as Record<string, any>),
            }));

            setColumns(generatedColumns);
            setCsvData(tableData);
            setFileList([csvFile]);
            setFileUploaded?.(true);
            if (edit !== "true") {
              message.success("Post-test CSV loaded successfully");
            }
          },
          error: (error) => {
            console.error("CSV parsing error:", error);
            setCsvData([]);
            setFileUploaded?.(false);
          },
        });
      } catch (error: any) {
        // Silently handle 404/500 errors for new jobs (file doesn't exist yet)
        // Only show error for actual failures in edit/view mode
        const status = error?.response?.status;
        if (status !== 404 && status !== 500) {
          console.error("Error checking post-test data:", error);
          message.error("Failed to load post-test CSV");
        }
        setFileUploaded?.(false);
      } finally {
        setLoading(false);
      }
    };

    const disabledDate = (current: Dayjs) => {
      return current && current > dayjs().endOf("day");
    };

    // Derived from whatever CSV is currently loaded (fresh upload or a
    // reopened/edit-mode study) - used to warn if the chosen Campaign Start
    // Date doesn't line up with the uploaded data. Previously a mismatch
    // here was silent: the backend would fall back to the nearest available
    // date (or, before that fix, crash outright) with no indication to the
    // user that their chosen date wasn't actually used as typed.
    const uploadedDateRange = useMemo(() => {
      if (!csvData || csvData.length === 0) return null;
      const parsedDates = csvData
        .map((row: any) => row.date)
        .filter((d: any) => d !== undefined && d !== null && d !== "")
        .map((d: string) => dayjs(d))
        .filter((d: Dayjs) => d.isValid());

      if (parsedDates.length === 0) return null;

      const sortedDates = [...parsedDates].sort(
        (a, b) => a.valueOf() - b.valueOf()
      );
      const uniqueDatesSet = new Set(
        sortedDates.map((d) => d.format("YYYY-MM-DD"))
      );

      return {
        sortedDates,
        uniqueDatesSet,
        min: sortedDates[0],
        max: sortedDates[sortedDates.length - 1],
      };
    }, [csvData]);

    const campaignDateWarning = useMemo(() => {
      if (!campaignStartDate || !uploadedDateRange) return null;

      const target = campaignStartDate.format("YYYY-MM-DD");
      if (uploadedDateRange.uniqueDatesSet.has(target)) return null;

      const minLabel = uploadedDateRange.min.format("YYYY-MM-DD");
      const maxLabel = uploadedDateRange.max.format("YYYY-MM-DD");

      if (
        campaignStartDate.isBefore(uploadedDateRange.min, "day") ||
        campaignStartDate.isAfter(uploadedDateRange.max, "day")
      ) {
        return {
          type: "error" as const,
          message: `Campaign start date (${target}) is outside the date range of your uploaded data (${minLabel} to ${maxLabel}). This won't produce a valid pre/post split - please pick a date within your data's range.`,
        };
      }

      // Find the nearest available date so the message is actionable.
      let nearest = uploadedDateRange.sortedDates[0];
      let nearestDiff = Math.abs(campaignStartDate.diff(nearest, "day"));
      for (const d of uploadedDateRange.sortedDates) {
        const diff = Math.abs(campaignStartDate.diff(d, "day"));
        if (diff < nearestDiff) {
          nearest = d;
          nearestDiff = diff;
        }
      }

      return {
        type: "warning" as const,
        message: `Campaign start date (${target}) does not match any date in your uploaded data. The nearest available date is ${nearest.format(
          "YYYY-MM-DD"
        )} - that will be used instead.`,
      };
    }, [campaignStartDate, uploadedDateRange]);

    const handleBauSpentChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      const value = e.target.value;

      // Allow empty while editing
      if (value === "") {
        setBauSpent("");
        return;
      }

      // Allow numbers with up to 2 decimal places
      if (!/^\d*\.?\d{0,2}$/.test(value)) {
        return;
      }

      const numericValue = Number(value);

      // Prevent negative values
      if (numericValue < 0) {
        return;
      }

      setBauSpent(value);
    };

    const handleStatSignThresholdChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      const value = e.target.value;

      // Allow empty while editing
      if (value === "") {
        setStatSigThreshold("");
        return;
      }

      // Allow only numbers with max 2 decimal places
      if (!/^\d*\.?\d{0,2}$/.test(value)) {
        return;
      }

      const numericValue = Number(value);

      // Prevent values greater than 100 only
      if (numericValue > 100) {
        return;
      }

      setStatSigThreshold(value);
    };

    const handleLoadFromDatabase = async (params: {
      startDate: string;
      endDate: string;
      aggregationLevel: 'city' | 'state' | 'dma';
    }) => {
      if (!jobConfigId) {
        message.error("Job configuration is not initialized");
        return;
      }

      setLoadingDatabase(true);
      setLoading(true);
      try {
        const response = await axiosInstance.post(
          `/model/geo_lift/loadFromDatabaseAndProcess/${jobConfigId}`,
          null,
          {
            params: {
              start_date: params.startDate,
              end_date: params.endDate,
              aggregation_level: params.aggregationLevel
            }
          }
        );

        if (response.data?.success && response.data?.data) {
          // Validate data sufficiency
          const uniqueLocations = response.data.summary?.unique_locations || 0;
          const uniqueDates = response.data.summary?.unique_dates || 0;

          if (uniqueLocations < MIN_GEO_LIFT_LOCATIONS || uniqueDates < MIN_GEO_LIFT_TIME_PERIODS) {
            Modal.error({
              title: "Insufficient Data",
              content: (
                <div>
                  <p>Your data does not meet the minimum requirements for Geo-Lift analysis:</p>
                  <ul style={{ marginTop: 12, paddingLeft: 20 }}>
                    <li>
                      <strong>Locations:</strong> {uniqueLocations} found (minimum {MIN_GEO_LIFT_LOCATIONS} required)
                      {uniqueLocations < MIN_GEO_LIFT_LOCATIONS && (
                        <span style={{ color: "red" }}> - Insufficient</span>
                      )}
                    </li>
                    <li>
                      <strong>Time Periods:</strong> {uniqueDates} found (minimum {MIN_GEO_LIFT_TIME_PERIODS} required)
                      {uniqueDates < MIN_GEO_LIFT_TIME_PERIODS && (
                        <span style={{ color: "red" }}> - Insufficient</span>
                      )}
                    </li>
                  </ul>
                  <p style={{ marginTop: 12 }}>
                    Please select a wider date range or use a different aggregation level.
                  </p>
                </div>
              ),
              okText: "Got it",
            });
            setLoadingDatabase(false);
            setLoading(false);
            return;
          }

          setLocationData(response.data.data);

          // Convert location data to CSV format for display
          const csvRows = response.data.data.map((loc: any) => ({
            location: loc.locationOriginal,
            date: loc.date || 'N/A',
            Y: loc.Y || 0
          }));

          setCsvData(csvRows);
          setColumns([
            { title: 'Location', dataIndex: 'location' },
            { title: 'Date', dataIndex: 'date' },
            { title: 'Revenue (Y)', dataIndex: 'Y' }
          ]);

          // Mark as uploaded
          setFileUploaded?.(true);
          setShowDatabaseModal(false);

          message.success(
            `Loaded ${response.data.summary.total_records} records from database (${params.startDate} to ${params.endDate})`
          );
        } else {
          message.error("Failed to load data from database");
        }
      } catch (error: any) {
        console.error("Error loading from database:", error);
        message.error(
          error?.response?.data?.detail || "Failed to load data from database"
        );
        setFileUploaded?.(false);
      } finally {
        setLoadingDatabase(false);
        setLoading(false);
      }
    };

    useEffect(() => {
      if (initialValues?.fileList.length) {
        const file: any = initialValues.fileList[0];
        if (file) {
          Papa.parse(file, {
            complete: (result) => {
              if (result.data.length > 0) {
                const headers = result.data[0] as string[];
                const generatedColumns = buildPreviewColumns(headers);

                const tableData = result.data
                  .slice(1)
                  .map((row: any, index) => ({
                    key: index,
                    ...headers.reduce((acc, header, i) => {
                      acc[header.toLowerCase().replace(/\s+/g, "_")] = row[i];
                      return acc;
                    }, {} as Record<string, any>),
                  }));

                setColumns(generatedColumns);
                setCsvData(tableData);
              }
            },
            error: (error) => {
              console.error("CSV parsing error:", error);
              setCsvData([]);
            },
            skipEmptyLines: true,
          });
        }
      }
    }, [initialValues?.fileList]);

    useEffect(() => {
      if (fileList.length > 0 && csvData.length === 0) {
        const file = fileList[0];
        if (file) {
          Papa.parse(file, {
            complete: (result) => {
              if (result.data.length > 0) {
                const headers = result.data[0] as string[];
                const generatedColumns = buildPreviewColumns(headers);

                const tableData = result.data
                  .slice(1)
                  .map((row: any, index) => ({
                    key: index,
                    ...headers.reduce((acc, header, i) => {
                      acc[header.toLowerCase().replace(/\s+/g, "_")] = row[i];
                      return acc;
                    }, {} as Record<string, any>),
                  }));

                setColumns(generatedColumns);
                setCsvData(tableData);
              }
            },
            error: (error) => {
              console.error("CSV parsing error:", error);
              setCsvData([]);
            },
            skipEmptyLines: true,
          });
        }
      }
    }, [fileList]);

    const handleTestNameChange = (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setTestName(newValue);
    };

    const handleFileUpload = async (
      file: File,
      mappings: { [key: string]: string | null }
    ) => {
      setLoading(true);
      try {
        await geoLiftJobConfigService.uploadFile(
          file,
          `${jobConfigId}/input/geo_lift_data.csv`
        );
        await new Promise((resolve, reject) => {
          Papa.parse(file, {
            complete: (result) => {
              if (result.data.length > 0) {
                const headers = result.data[0] as string[];
                const generatedColumns = buildPreviewColumns(headers);

                const tableData = result.data
                  .slice(1)
                  .map((row: any, index) => ({
                    key: index,
                    ...headers.reduce((acc, header, i) => {
                      acc[header.toLowerCase().replace(/\s+/g, "_")] = row[i];
                      return acc;
                    }, {} as Record<string, any>),
                  }));

                setColumns(generatedColumns);
                setCsvData(tableData);
                setFileList([file]);
                setFileUploaded?.(true);
                setColumnMappings(mappings);

                message.success(
                  `${file.name} uploaded and mapped successfully`
                );

                resolve(true);
              } else {
                message.error("Empty CSV or invalid format.");
                setFileUploaded?.(false);
                reject(new Error("Empty CSV"));
              }
            },
            error: (error) => {
              console.error("CSV parsing error:", error);
              setCsvData([]);
              setFileUploaded?.(false);
              reject(error);
            },
            skipEmptyLines: true,
          });
        });
      } catch (error) {
        console.error("Upload failed:", error);
        message.error("File upload failed");
        setFileUploaded?.(false);
        throw error;
      } finally {
        setLoading(false);
      }
    };

    const handleMappingConfirm = async (mappings: ColumnMapping[]) => {
      if (!pendingFile) {
        message.error("No file selected");
        return;
      }

      const backendMappings = convertMappingsToBackendFormat(mappings);
      setShowMappingModal(false);
      setPendingBackendMappings(backendMappings);
      setValidatingData(true);
      setShowPreviewModal(true);

      try {
        // Rename columns to the canonical names (date/location/Y/adspend)
        // the backend expects, based on what the user just confirmed in the
        // mapping modal - this is the file that actually gets validated and
        // (if valid) uploaded, so what's checked is exactly what's used.
        const canonicalFile = await applyColumnMapping(pendingFile, mappings);
        setCanonicalFileForUpload(canonicalFile);

        const result = await geoLiftJobConfigService.validateData(
          canonicalFile,
          campaignStartDate?.format("YYYY-MM-DD")
        );
        setDataValidationResponse(result);
      } catch (error) {
        console.error("Data validation failed:", error);
        message.error("Failed to validate file. Please try again.");
        setShowPreviewModal(false);
      } finally {
        setValidatingData(false);
      }
    };

    const handlePreviewConfirm = async () => {
      if (!canonicalFileForUpload || !pendingBackendMappings) {
        message.error("No validated file to upload");
        return;
      }
      setShowPreviewModal(false);
      try {
        await handleFileUpload(canonicalFileForUpload, pendingBackendMappings);
        setTimeout(async () => {
          await processLocationsAfterUpload();
        }, 2000);
      } catch (error) {
        console.error("Upload failed after validation:", error);
      } finally {
        setPendingFile(null);
        setCanonicalFileForUpload(null);
        setPendingBackendMappings(null);
        setDataValidationResponse(null);
      }
    };

    const handlePreviewCancel = () => {
      setShowPreviewModal(false);
      setPendingFile(null);
      setCanonicalFileForUpload(null);
      setPendingBackendMappings(null);
      setDataValidationResponse(null);
      setValidationResult(null);
      message.info("File upload cancelled");
    };

    const handleMappingCancel = () => {
      setShowMappingModal(false);
      setPendingFile(null);
      setValidationResult(null);
      message.info("File upload cancelled");
    };

    const uploadProps: UploadProps = {
      name: "file",
      accept: ".csv",
      showUploadList: false, // Add this line
      beforeUpload: async (file) => {
        setLoading(true);
        try {
          await new Promise((resolve, reject) => {
            Papa.parse(file, {
              preview: 1,
              header: true,
              complete: (result) => {
                if (result.meta.fields && result.meta.fields.length > 0) {
                  const headers = result.meta.fields;
                  const validation = validateGeoLiftData(headers);
                  setValidationResult(validation);
                  setPendingFile(file);
                  setShowMappingModal(true);
                  setLoading(false);
                  resolve(true);
                } else {
                  message.error("Empty CSV or invalid format.");
                  setFileUploaded?.(false);
                  setLoading(false);
                  reject(new Error("Empty CSV"));
                }
              },
              error: (error) => {
                console.error("CSV parsing error:", error);
                setFileUploaded?.(false);
                setLoading(false);
                reject(error);
              },
              skipEmptyLines: true,
            });
          });
        } catch (error) {
          console.error("Upload failed:", error);
          message.error("File upload failed");
          setFileUploaded?.(false);
          setLoading(false);
        }

        return false;
      },
    };

    type RawItem = { location: string; totalSales: number; count: number };
    type StateDataItem = { state: string; value: number };
    type CityDataItem = {
      name: string;
      coordinates: [number, number];
      value: number;
    };

    const US_STATE_ABBR = new Set([
      "AL",
      "AK",
      "AZ",
      "AR",
      "CA",
      "CO",
      "CT",
      "DC",
      "DE",
      "FL",
      "GA",
      "HI",
      "IA",
      "ID",
      "IL",
      "IN",
      "KS",
      "KY",
      "LA",
      "MA",
      "MD",
      "ME",
      "MI",
      "MN",
      "MO",
      "MS",
      "MT",
      "NC",
      "ND",
      "NE",
      "NH",
      "NJ",
      "NM",
      "NV",
      "NY",
      "OH",
      "OK",
      "OR",
      "PA",
      "RI",
      "SC",
      "SD",
      "TN",
      "TX",
      "UT",
      "VA",
      "VT",
      "WA",
      "WI",
      "WV",
      "WY",
    ]);

    const REGION_TO_STATES: Record<string, string[]> = {
      "New England": ["MA", "CT", "RI", "NH", "VT", "ME"],
      "West Texas/New Mexico": ["TX", "NM"],
    };

    const LOCATION_OVERRIDES: Record<
      string,
      { cities?: Array<{ city: string; state: string }>; states?: string[] }
    > = {
      "Baltimore/Washington": {
        cities: [
          { city: "Baltimore", state: "MD" },
          { city: "Washington", state: "DC" },
        ],
      },
      "New Orleans/Mobile": {
        cities: [
          { city: "New Orleans", state: "LA" },
          { city: "Mobile", state: "AL" },
        ],
      },
      "West Texas/New Mexico": { states: ["TX", "NM"] },
      "New England": { states: ["MA", "CT", "RI", "NH", "VT", "ME"] },
    };

    const cleanTail = (s: string) =>
      s.replace(/\s*-\s*MultiOutletC\s*$/i, "").trim();

    const hash32 = (s: string) => {
      let h = 2166136261 >>> 0;
      for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      return h >>> 0;
    };

    const seeded01 = (seedStr: string) => {
      let h = hash32(seedStr);
      h ^= h << 13;
      h ^= h >>> 17;
      h ^= h << 5;
      return ((h >>> 0) % 1_000_000) / 1_000_000;
    };

    const randomUSCoord = (key: string): [number, number] => {
      const r1 = seeded01(key + "::lon");
      const r2 = seeded01(key + "::lat");
      const lon = -124.7 + r1 * 57.8;
      const lat = 24.5 + r2 * 25.0;
      return [parseFloat(lon.toFixed(4)), parseFloat(lat.toFixed(4))];
    };

    const expandMultiCitySameState = (cityPart: string, state: string) =>
      cityPart
        .split("/")
        .map((c) => ({ city: c.trim().replace(/\s+/g, " "), state }));

    const tryParseCityState = (
      text: string
    ): Array<{ city: string; state: string }> | null => {
      const m = text.match(/^(.*?),\s*([A-Z]{2})$/i);
      if (!m) return null;
      const rawCity = m[1].trim();
      const state = m[2].toUpperCase();
      if (!US_STATE_ABBR.has(state)) return null;
      return expandMultiCitySameState(rawCity, state);
    };

    const tryParseStateOnly = (text: string): string | null => {
      const m = text.match(/^[A-Za-z.\s]+,\s*([A-Z]{2})$/);
      if (!m) return null;
      const st = m[1].toUpperCase();
      return US_STATE_ABBR.has(st) ? st : null;
    };

    const normalizeLocation = (
      raw: string
    ): {
      cities?: Array<{ city: string; state: string }>;
      states?: string[];
    } => {
      const cleaned = cleanTail(raw);

      if (LOCATION_OVERRIDES[cleaned]) return LOCATION_OVERRIDES[cleaned];

      if (REGION_TO_STATES[cleaned])
        return { states: REGION_TO_STATES[cleaned] };

      const cities = tryParseCityState(cleaned);
      if (cities?.length) return { cities };

      const st = tryParseStateOnly(cleaned);
      if (st) return { states: [st] };

      // Check if it's a full state name (e.g., "California", "New York")
      const stateCode = STATE_NAME_TO_POSTAL[cleaned];
      if (stateCode) return { states: [stateCode] };

      return {};
    };

    function transformSalesToStateAndCity(rows: RawItem[]): {
      US_STATE_DATA: StateDataItem[];
      US_CITY_DATA: CityDataItem[];
    } {
      const stateTotals = new Map<string, number>();
      const cityPoints: CityDataItem[] = [];

      for (const row of rows) {
        const parsed = normalizeLocation(row.location);
        const total = row.totalSales ?? 0;
        if (parsed.cities?.length) {
          const cityShare = total / parsed.cities.length;

          const statesInRow = Array.from(
            new Set(parsed.cities.map((c) => c.state))
          );
          const stateShare = total / statesInRow.length;

          for (const st of statesInRow) {
            stateTotals.set(st, (stateTotals.get(st) ?? 0) + stateShare);
          }

          for (const { city, state } of parsed.cities) {
            const name = `${city}, ${state}`;
            const coordinates = randomUSCoord(name);
            cityPoints.push({ name, coordinates, value: cityShare });
          }
        } else if (parsed.states?.length) {
          const perState = total / parsed.states.length;
          for (const st of parsed.states) {
            stateTotals.set(st, (stateTotals.get(st) ?? 0) + perState);
          }
        }
      }

      const US_STATE_DATA: StateDataItem[] = Array.from(stateTotals.entries())
        .map(([state, value]) => ({ state, value }))
        .sort((a, b) => b.value - a.value);

      const US_CITY_DATA: CityDataItem[] = cityPoints.sort(
        (a, b) => b.value - a.value
      );

      return { US_STATE_DATA, US_CITY_DATA };
    }

    const { US_STATE_DATA } = transformSalesToStateAndCity(result);
    const [locationData, setLocationData] = useState<any[]>([]);
    const [loadingLocations, setLoadingLocations] = useState(false);

    // The actual live geocode call (POST processLocations) - does NOT touch
    // selectedMarketsIncludes. Shared by the fresh-upload path (which adds
    // its own DTW preselection on top) and view mode's cache-miss fallback
    // (which must never auto-preselect - an already-saved study's selection
    // comes from the loaded job config, not a fresh guess). Returns whether
    // at least one location actually got usable coordinates, so callers can
    // tell a real success from "ran, but everything failed again".
    const runLiveGeocode = async (): Promise<boolean> => {
      if (!jobConfigId) {
        return false;
      }

      setLoadingLocations(true);
      try {
        const response = await axiosInstance.post(
          `/model/geo_lift/processLocations/${jobConfigId}`
        );

        if (response.data?.success && response.data?.data) {
          setLocationData(response.data.data);
          if (response.data.warning) {
            message.warning(response.data.warning);
          }
          return response.data.data.some((loc: any) => loc.lat != null && loc.long != null);
        }
        return false;
      } catch (err: any) {
        // Silently handle 404/500 errors for new jobs (file doesn't exist yet)
        const status = err?.response?.status;
        if (status !== 404 && status !== 500) {
          console.error("Error processing location data:", err);
          message.error("Failed to process location data");
        }
        return false;
      } finally {
        setLoadingLocations(false);
      }
    };

    const processLocationsAfterUpload = async () => {
      await runLiveGeocode();
      // No longer needs the just-fetched data passed in - preselection now
      // matches against `result` (from the raw uploaded CSV, already fresh
      // independent of this geocoding call), not `locationData`.
      getPreselectedTestMarkets();
    };

    // View/edit mode: read the location data already geocoded and cached in
    // S3 during the original upload (getLatLong/{id} reads
    // data_with_latlong.csv, the same file processLocationsAfterUpload
    // writes) instead of unconditionally re-running live geocoding on every
    // view. Never calls getPreselectedTestMarkets() - an already-saved
    // study's selected markets come from the loaded job config, not DTW
    // auto-preselection.
    //
    // Cache-only would strand any study whose original geocoding attempt
    // failed outright (confirmed for real: a study created while the live
    // geocoder was down has a cached file with 49/49 rows and zero
    // coordinates - that's not a bug, it's an accurate cache of a failed
    // attempt, but reading it forever with no retry means that study's map
    // is permanently broken with no way to recover). So: if the cache comes
    // back with no usable coordinates at all, fall back to one live
    // geocode attempt via runLiveGeocode() (which now has retry-with-backoff
    // - see location_service.py - giving it a real chance this time),
    // still without touching selectedMarketsIncludes.
    const loadCachedLocationData = async () => {
      if (!jobConfigId) {
        return;
      }

      setLoadingLocations(true);
      try {
        const response = await axiosInstance.get(
          `/model/geo_lift/getLatLong/${jobConfigId}`
        );

        if (response.data?.success && Array.isArray(response.data?.data)) {
          // getLatLong (unlike processLocations) returns latitude/longitude,
          // not lat/long - normalize here rather than changing that shared
          // endpoint, which ViewTestAndControlMarket/PowerAnalysisExperiment
          // also depend on in its current shape.
          const normalized = response.data.data.map((loc: any) => ({
            ...loc,
            lat: loc.lat ?? loc.latitude,
            long: loc.long ?? loc.longitude,
          }));

          const hasAnyCoordinates = normalized.some(
            (loc: any) => loc.lat != null && loc.long != null
          );

          if (hasAnyCoordinates || normalized.length === 0) {
            setLocationData(normalized);
            if (response.data.warning) {
              message.warning(response.data.warning);
            }
          } else {
            console.warn(
              `[loadCachedLocationData] Cached data for ${jobConfigId} has 0/${normalized.length} ` +
              `usable coordinates - falling back to a live geocode attempt`
            );
            await runLiveGeocode();
          }
        }
      } catch (err: any) {
        const status = err?.response?.status;
        if (status !== 404 && status !== 500) {
          console.error("Error loading cached location data:", err);
        }
      } finally {
        setLoadingLocations(false);
      }
    };

    const cityDataForMap = useMemo(() => {
      if (!locationData || locationData.length === 0) {
        console.log('[cityDataForMap] No locationData available');
        return [];
      }

      console.log('[cityDataForMap] Processing', locationData.length, 'locations');
      console.log('[cityDataForMap] Sample location:', locationData[0]);

      // Reverse mapping: abbreviation -> full name (was rebuilt on every
      // .map() iteration below - moved out since it's the same every time).
      const POSTAL_TO_STATE_NAME: Record<string, string> = {};
      for (const [fullName, abbr] of Object.entries(STATE_NAME_TO_POSTAL)) {
        POSTAL_TO_STATE_NAME[abbr] = fullName;
      }

      // Which states have at least one selected market - computed once, by
      // state identity rather than text similarity. selectedMarketsIncludes
      // holds raw CSV text (e.g. "Albany, NY"); comparing that against
      // loc.locationOriginal (the geocoded/normalized name, e.g.
      // "albany,new york") via fuzzy text matching was unreliable - it only
      // "worked" when a state's abbreviation happened to be a literal prefix
      // of its full name (e.g. "OH" of "Ohio"), which is why Ohio's markets
      // highlighted but most others didn't. This map only colors by state
      // anyway (see the per-state highlighting caveat), so matching by the
      // state itself is both simpler and actually correct.
      //
      // Raw CSV location values come in more than one shape depending on the
      // file - confirmed against two real files: DMA-style "City, ST" (the
      // Tatari file - trailing 2-letter abbreviation after the last comma)
      // and plain full state names with no comma at all (restore-geo-lift.csv
      // - "Alabama", "California", ...). The first version of this only
      // handled the DMA-style comma+abbreviation case, so a plain-state-name
      // file like restore-geo-lift.csv never populated selectedStateNames at
      // all - Select Markets worked, but nothing highlighted on the map.
      // Also handles a bare 2-letter abbreviation with no comma, in case a
      // future file uses that.
      const stateNamesList = Object.keys(STATE_NAME_TO_POSTAL);
      const selectedStateNames = new Set<string>();
      for (const sel of selectedMarketsIncludes) {
        const trimmed = sel.trim();

        // Case 1: already a plain full state name (e.g. "Alabama")
        const directIdx = stateNamesList.findIndex(
          (s) => s.toLowerCase() === trimmed.toLowerCase()
        );
        if (directIdx !== -1) {
          selectedStateNames.add(stateNamesList[directIdx]);
          continue;
        }

        // Case 2: bare 2-letter state abbreviation (e.g. "OH")
        if (trimmed.length === 2 && POSTAL_TO_STATE_NAME[trimmed.toUpperCase()]) {
          selectedStateNames.add(POSTAL_TO_STATE_NAME[trimmed.toUpperCase()]);
          continue;
        }

        // Case 3: DMA-style "City, ST" - trailing 2-letter abbreviation
        const parts = trimmed.split(',').map((p: string) => p.trim());
        const lastPart = parts[parts.length - 1];
        if (lastPart && lastPart.length === 2) {
          const fullStateName = POSTAL_TO_STATE_NAME[lastPart.toUpperCase()];
          if (fullStateName) {
            selectedStateNames.add(fullStateName);
            continue;
          }
        }

        // Case 4: DMA-level values, e.g. "NY-DMA" or "DMA-100-NY"
        const upperTrimmed = trimmed.toUpperCase();
        let dmaStateCode: string | null = null;
        if (upperTrimmed.endsWith('-DMA') && upperTrimmed.length > 4) {
          dmaStateCode = upperTrimmed.slice(0, -4);
        } else if (upperTrimmed.startsWith('DMA-')) {
          const dmaParts = upperTrimmed.split('-');
          if (dmaParts.length >= 3) {
            dmaStateCode = dmaParts[dmaParts.length - 1];
          }
        }
        if (dmaStateCode && POSTAL_TO_STATE_NAME[dmaStateCode]) {
          selectedStateNames.add(POSTAL_TO_STATE_NAME[dmaStateCode]);
        }
      }

      const mappedData = locationData
        .filter((loc) => loc.lat && loc.long)
        .map((loc, index) => {
          let salesValue = 0;

          // If location data already has Y value (from database load or CSV upload), use it
          if (loc.Y !== undefined && loc.Y !== null) {
            salesValue = loc.Y;
            if (index < 3) {
              console.log(`[cityDataForMap] Location ${index}: Using Y value:`, salesValue, 'for', loc.locationOriginal);
            }
          } else {
            // Otherwise, try to match with CSV data (for file uploads)
            const marketSales = result || [];
            for (const market of marketSales) {
              if (market.location && loc.locationOriginal) {
                if (fuzzyMatchLocation(market.location, loc.locationOriginal)) {
                  salesValue = market.totalSales || 0;
                  if (index < 3) {
                    console.log(`[cityDataForMap] Location ${index}: Matched with CSV:`, salesValue, 'for', loc.locationOriginal);
                  }
                  break;
                }
              }
            }
            if (salesValue === 0 && index < 3) {
              console.log(`[cityDataForMap] Location ${index}: No match found for`, loc.locationOriginal);
            }
          }

          // Extract state name from locationAddress for map matching
          // locationAddress format: "City, State Abbr, Country" or full address with state
          let stateName = "Unknown";

          if (loc.locationAddress) {
            // Try to extract state from address (e.g., "Atlanta, GA, United States" -> "Georgia").
            // The live geocoder doesn't always return a clean 2-letter code -
            // real results for this file included both ("...Atlanta, GA,
            // United States") and full-name-only ("...Cleveland, Ohio,
            // United States") forms, so both branches below are needed, not
            // just the abbreviation one.
            const addressParts = loc.locationAddress.split(',').map((p: string) => p.trim());

            // Look for state abbreviation or full state name in address parts
            for (const part of addressParts) {
              // Check if it's a 2-letter state code (e.g., "GA", "NC")
              if (part.length === 2 && part === part.toUpperCase()) {
                const fullStateName = POSTAL_TO_STATE_NAME[part];
                if (fullStateName) {
                  stateName = fullStateName;
                  break;
                }
              }
              // Check if it's already a full state name - was comparing an
              // as-cased `part` (e.g. "Ohio") against an all-lowercase list
              // ("ohio"), which never matched (Array.includes is
              // case-sensitive), so this branch silently never fired. Fixed
              // to lowercase `part` before comparing, and to resolve to the
              // canonical Title-Case name (stateNames[idx]) rather than
              // whatever casing the geocoder happened to return.
              const stateNames = Object.keys(STATE_NAME_TO_POSTAL);
              const matchIdx = stateNames.findIndex(
                (state) => state.toLowerCase() === part.toLowerCase()
              );
              if (matchIdx !== -1) {
                stateName = stateNames[matchIdx];
                break;
              }
            }
          }

          if (index < 3) {
            console.log(`[cityDataForMap] Location ${index}: Extracted state name "${stateName}" from "${loc.locationAddress}"`);
          }

          return {
            name: stateName,
            coordinates: [loc.long, loc.lat] as [number, number],
            value: salesValue > 0 ? salesValue : 100,
            isState: true,
            // Only markets checked in "Select Markets" should be highlighted
            // on this map - see WorldHoldoutTreatmentMap.tsx's fill logic.
            // Matched by state identity (selectedStateNames, computed above)
            // rather than fuzzy text matching against loc.locationOriginal -
            // see the comment above selectedStateNames for why.
            selected: selectedStateNames.has(stateName),
          };
        });

      console.log('[cityDataForMap] Total mapped locations:', mappedData.length);
      console.log('[cityDataForMap] Sample mapped data:', mappedData.slice(0, 3));

      return mappedData;
    }, [locationData, result, selectedMarketsIncludes]);
    const getPreselectedTestMarkets = async () => {
      try {
        const response = await axiosInstance.post(GEO_LIFT_GET_TEST_MARKET, {
          start_campaign_date: new Date(campaignStartDate as any)
            .toISOString()
            .split("T")[0],
          job_config_id: jobConfigId,
        });
        const arrayOfStrings: string[] = Object.values(
          response.data.market_names
        );
        // getTestMarkets' centroid_markets come from the exact same raw
        // uploaded CSV as `result` (summarizeMarkets(csvData)) - DTW
        // clustering reads geo_lift_data.csv directly, same source, so these
        // match `result[i].location` exactly (trim only, no fuzzy matching
        // needed - that was only required back when the Select's options
        // came from the geocoded/normalized locationOriginal instead).
        setSelectedMarketsIncludes(arrayOfStrings?.map((ele) => ele?.trim()));
      } catch (err) {
        console.log(err);
      }
    };
    useEffect(() => {
      // Load CSV data for edit mode AND view mode (existing jobs)
      // For new jobs: mode=evaluate or mode=plan is in URL (and edit is NOT true)
      // For edit mode: edit=true is in URL
      // For view mode: no mode parameter and no edit parameter
      const modeParam = searchParams.get("mode");
      const isEditMode = edit === "true";
      const isNewJob = !!modeParam && !isEditMode; // New job has mode but NOT edit=true
      const isViewMode = !modeParam && !isEditMode; // View mode has neither mode nor edit

      if (jobConfigId && (isEditMode || isViewMode)) {
        checkPostTestData();
      }
    }, [jobConfigId, edit, searchParams]);
    return (
      <div
        className=" mx-auto px-4 sm:px-2 lg:px-3 py-2 space-y-4"
        style={viewMode ? { pointerEvents: "none", opacity: 0.75 } : undefined}
      >
        {/* Header Info Banner */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 shadow-sm border border-blue-100">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 mt-1">
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center shadow-md">
                <CustomExcelIcon className="text-white text-2xl" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Upload Campaign Data
              </h3>
              <p className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                <CheckCircleOutlined className="text-blue-500" />
                <span>
                  <span className="font-semibold">CSV only</span>, column
                  headers in the first row · at least{" "}
                  <span className="font-semibold">{MIN_GEO_LIFT_LOCATIONS} locations</span> and{" "}
                  <span className="font-semibold">{MIN_GEO_LIFT_TIME_PERIODS} time periods</span>
                </span>
              </p>

              <div className="bg-white rounded-lg border border-blue-100 overflow-hidden text-sm">
                <div className="overflow-x-auto">
                <table className="w-full text-left min-w-[560px]">
                  <thead>
                    <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                      <th className="px-3 py-2 font-semibold">Column</th>
                      <th className="px-3 py-2 font-semibold">Required</th>
                      <th className="px-3 py-2 font-semibold">Expected format</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr>
                      <td className="px-3 py-2 font-mono text-xs text-gray-800">location</td>
                      <td className="px-3 py-2 text-gray-600">Yes</td>
                      <td className="px-3 py-2 text-gray-600">
                        State name, state abbreviation, DMA code, or clean "City, State" (no
                        extra text after the state)
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 font-mono text-xs text-gray-800">date</td>
                      <td className="px-3 py-2 text-gray-600">Yes</td>
                      <td className="px-3 py-2 text-gray-600">
                        <span className="font-semibold text-gray-700">YYYY-MM-DD</span> or{" "}
                        <span className="font-semibold text-gray-700">MM/DD/YYYY</span>
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 font-mono text-xs text-gray-800">Y</td>
                      <td className="px-3 py-2 text-gray-600">Yes</td>
                      <td className="px-3 py-2 text-gray-600">
                        KPI / conversions as a plain number
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 font-mono text-xs text-gray-800">adspend</td>
                      <td className="px-3 py-2 text-gray-600">Optional</td>
                      <td className="px-3 py-2 text-gray-600">
                        Plain number, same format as Y.
                      </td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>

              <Button
                type="link"
                icon={<DownloadOutlined />}
                onClick={downloadGeoLiftSampleTemplate}
                className="px-0 mt-2 h-auto"
                style={{ color: "#2563EB" }}
              >
                Download sample CSV template
              </Button>
            </div>
          </div>
        </div>

        {mode === "evaluate" || edit === "true" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Test Name Input */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-3">
                <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-sm">
                  1
                </span>
                Test Name
                <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="e.g., Synthetic Control Test Q4 2024"
                value={geoLiftStudyName}
                onChange={handleTestNameChange}
                size="large"
                className="rounded-lg"
              />
            </div>

            {/* Campaign Start Date */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-3">
                <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-sm">
                  2
                </span>
                <CalendarOutlined className="text-indigo-600" />
                Campaign Start Date
                <span className="text-red-500">*</span>
              </label>
              <DatePicker
                placeholder="Select when your campaign started"
                value={campaignStartDate}
                onChange={handleDateChange}
                disabledDate={disabledDate}
                size="large"
                className="w-full rounded-lg"
                format="YYYY-MM-DD"
                showToday={false}
              />
              <Text className="text-gray-500 text-sm mt-2 block">
                Select the date when your campaign started (past dates only)
              </Text>
              {campaignDateWarning && (
                <Alert
                  className="mt-3"
                  type={campaignDateWarning.type}
                  showIcon
                  message={campaignDateWarning.message}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-3">
              <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-sm">
                1
              </span>
              Test Name
              <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="e.g., Synthetic Control Test Q4 2024"
              value={geoLiftStudyName}
              onChange={handleTestNameChange}
              size="large"
              className="rounded-lg"
            />
          </div>
        )}

        {/* BAU Spent Input */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-3">
            <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-sm">
              {mode === "evaluate" ? "3" : "2"}
            </span>
              <DollarOutlined className="text-green-600" />
                Average Monthly BAU Media Spent
              <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Enter Average Monthly BAU Media Spent (e.g., 2000.50)"
            value={bauSpent}
            onChange={handleBauSpentChange}
            prefix={<span className="text-gray-500 font-medium">$</span>}
            size="large"
            type="text"
            className="rounded-lg"
          />
        </div>

        {/* Statistical Significance Threshold Input */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-3">
            <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-sm">
              {mode === "evaluate" ? "4" : "3"}
            </span>
              Statistical Significance Threshold %
          </label>
          <Input
            placeholder="Enter Statistical Significance Threshold % (50 to 100 inclusive)"
            value={statSigThreshold}
            onChange={handleStatSignThresholdChange}
            onBlur={() => {
              if (statSigThreshold === "") {
                setStatSigThreshold(
                  `${DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD}`
                );
                return;
              }

              const numericValue = Number(statSigThreshold);

              if (numericValue < 50) {
                setStatSigThreshold("50");
              }
            }}
            size="large"
            type="text"
            className="rounded-lg"
          />
        </div>

        {/* Data Source Selection */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-4">
            <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-sm">
              {mode === "evaluate" ? "5" : "4"}
            </span>
            Data Source
            <span className="text-red-500">*</span>
          </label>

          <Radio.Group
            onChange={(e) => setDataSource(e.target.value)}
            value={dataSource}
            className="w-full"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div
                className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
                  dataSource === "csv"
                    ? "border-indigo-500 bg-indigo-50 shadow-md"
                    : "border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30"
                }`}
              >
                <Radio value="csv" className="w-full">
                  <div className="ml-2">
                    <p className="font-semibold text-gray-800 flex items-center gap-2">
                      <CustomExcelIcon className="text-green-600" />
                      Upload CSV File
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Upload your own CSV with historical data
                    </p>
                  </div>
                </Radio>
              </div>

              <div
                className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
                  dataSource === "database"
                    ? "border-indigo-500 bg-indigo-50 shadow-md"
                    : "border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30"
                }`}
              >
                <Radio value="database" className="w-full">
                  <div className="ml-2">
                    <p className="font-semibold text-gray-800 flex items-center gap-2">
                      <CalendarOutlined className="text-blue-600" />
                      Use Database Data
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Load up to last 52 weeks of transaction data
                    </p>
                  </div>
                </Radio>
              </div>
            </div>
          </Radio.Group>
        </div>

        {/* Load from Database Button */}
        {dataSource === "database" && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 shadow-sm border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-800 mb-2">
                  Database Data Source
                </h4>
                <p className="text-sm text-gray-600">
                  Load transaction data from your database, automatically aggregated by location and date for Geo-Lift analysis. Default date range is 52 weeks (recommended).
                </p>
              </div>
              <Button
                type="primary"
                size="large"
                onClick={() => setShowDatabaseModal(true)}
                disabled={mode === "evaluate" && !campaignStartDate}
                className="ml-4"
              >
                Configure & Load
              </Button>
            </div>
            {mode === "evaluate" && !campaignStartDate && (
              <p className="text-sm text-amber-600 mt-3 flex items-center gap-2 bg-amber-50 p-3 rounded-lg">
                <span className="text-lg">⚠️</span>
                Please select campaign start date first
              </p>
            )}
          </div>
        )}

        {/* File Upload Section */}
        {dataSource === "csv" && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-4">
              <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-sm">
                {mode === "evaluate" ? "6" : "5"}
              </span>
              Upload Data File
              <span className="text-red-500">*</span>
            </label>

          {/* Existing File Alert */}
          {fileData && (
            <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 flex items-start gap-3">
              <CheckCircleOutlined className="text-green-600 text-xl mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">
                  Existing campaign data is loaded
                </p>
                <p className="text-xs text-green-600 mt-1">
                  You can upload a new file to replace it
                </p>
              </div>
            </div>
          )}
          {edit !== "true" && (
            <div
              className={`border-2 border-dashed rounded-lg transition-all ${
                mode === "evaluate" && !campaignStartDate
                  ? "border-gray-200 bg-gray-50 cursor-not-allowed opacity-60"
                  : "border-gray-300 hover:border-indigo-500 bg-white hover:bg-indigo-50/30 cursor-pointer"
              }`}
            >
              <Upload
                {...uploadProps}
                disabled={mode === "evaluate" && !campaignStartDate}
              >
                <div className="flex items-center gap-4 py-6 px-6">
                  <div
                    className={`flex items-center justify-center w-12 h-12 rounded-lg flex-shrink-0 ${
                      mode === "evaluate" && !campaignStartDate
                        ? "bg-gray-200"
                        : "bg-indigo-100"
                    }`}
                  >
                    <UploadOutlined
                      className={`text-2xl ${
                        mode === "evaluate" && !campaignStartDate
                          ? "text-gray-400"
                          : "text-indigo-600"
                      }`}
                    />
                  </div>
                  <div className="flex-1 text-left">
                    <div
                      className={`font-semibold text-base mb-1 ${
                        mode === "evaluate" && !campaignStartDate
                          ? "text-gray-400"
                          : "text-gray-700"
                      }`}
                    >
                      Click or drag file to upload
                    </div>
                    <p
                      className={`text-xs ${
                        mode === "evaluate" && !campaignStartDate
                          ? "text-gray-400"
                          : "text-gray-500"
                      }`}
                    >
                      {mode === "evaluate" && !campaignStartDate
                        ? "Please select campaign start date first"
                        : "Support for CSV file only"}
                    </p>
                  </div>
                </div>
              </Upload>
            </div>
          )}

          {/* Uploaded File Display */}
          {fileList.length > 0 && (
            <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 flex items-center justify-between hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <CustomExcelIcon className="text-white text-xl" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {fileList[0].name}
                  </p>
                  <p className="text-xs text-gray-500">
                    CSV File Uploaded Successfully
                  </p>
                </div>
              </div>
              <Button
                disabled={edit === "true"}
                type="text"
                danger
                onClick={() => {
                  setFileList([]);
                  setCsvData([]);
                  setLocationData([]);
                  setFileUploaded?.(false);
                }}
                icon={<CloseOutlined />}
                className="hover:bg-red-50"
              >
                Remove
              </Button>
            </div>
          )}

          {/* Data Preview Table */}
          {csvData.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <span className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <CustomExcelIcon className="text-purple-600" />
                  </span>
                  Data Preview
                </h3>
                <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  Showing first 5 rows
                </span>
              </div>
              <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                <Table
                  columns={columns}
                  loading={loading}
                  dataSource={csvData.slice(0, 5)}
                  pagination={false}
                  scroll={{ x: true }}
                  bordered
                  size="small"
                  className="w-full"
                />
              </div>
            </div>
          )}
          </div>
        )}

        {/* SCM Type Selection */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-4">
            <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-sm">
              {mode === "evaluate" ? "7" : "6"}
            </span>
            SCM Type
            <span className="text-red-500">*</span>
          </label>

          <Radio.Group
            disabled={!locationData.length}
            onChange={(e) => {
              setScmType(e.target.value);
              setSelectedMarketsIncludes([]);
            }}
            value={scmType}
            className="w-full"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div
                className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
                  scmType === "pooled"
                    ? "border-indigo-500 bg-indigo-50 shadow-md"
                    : "border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30"
                } ${
                  !locationData.length ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Radio value="pooled" className="w-full">
                  <div className="ml-2">
                    <p className="font-semibold text-gray-800">Pooled SCM</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Combine multiple markets for analysis
                    </p>
                  </div>
                </Radio>
              </div>

              <div
                className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
                  scmType === "single"
                    ? "border-indigo-500 bg-indigo-50 shadow-md"
                    : "border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30"
                } ${
                  !locationData.length ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Radio value="single" className="w-full">
                  <div className="ml-2">
                    <p className="font-semibold text-gray-800">Single SCM</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Analyze individual markets separately
                    </p>
                  </div>
                </Radio>
              </div>
            </div>
          </Radio.Group>
           {!locationData.length && (
            <p className="text-sm text-amber-600 mt-3 flex items-center gap-2 bg-amber-50 p-3 rounded-lg">
              <span className="text-lg">⚠️</span>
              Please upload a file first to enable SCM type selection
            </p>
          )}
        </div>



          {/* Regression Type Selection */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-4">
            <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-sm">
              {mode === "evaluate" ? "8" : "7"}
            </span>
            Regression Type
            <span className="text-red-500">*</span>
          </label>

          <Radio.Group
            disabled={!locationData.length}
            onChange={(e) => {
              setRegressionType(e.target.value);
              }}
            value={regressionType}
            className="w-full"
            defaultValue="SCM"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div
                className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
                  regressionType === "ridge"
                    ? "border-indigo-500 bg-indigo-50 shadow-md"
                    : "border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30"
                } ${
                  !locationData.length ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Radio value="Ridge ASCM" className="w-full">
                  <div className="ml-2">
                    <p className="font-semibold text-gray-800">Ridge ASCM</p>

                  </div>
                </Radio>
              </div>

              <div
                className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
                  regressionType === "normal"
                    ? "border-indigo-500 bg-indigo-50 shadow-md"
                    : "border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30"
                } ${
                  !locationData.length ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Radio value="SCM" className="w-full">
                  <div className="ml-2">
                    <p className="font-semibold text-gray-800">SCM</p>
                  </div>
                </Radio>
              </div>
            </div>
          </Radio.Group>



          {!locationData.length && (
            <p className="text-sm text-amber-600 mt-3 flex items-center gap-2 bg-amber-50 p-3 rounded-lg">
              <span className="text-lg">⚠️</span>
              Please upload a file first to enable Regression type selection
            </p>
          )}
        </div>

        {/* Study Type Selection */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          {/* Label */}
          <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-4">
            <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-sm">
              {mode === "evaluate" ? "9" : "8"}
            </span>
            Sales
          </label>

          {/* Card */}
          <div
            className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
              isSales
                ? "border-indigo-500 bg-indigo-50 shadow-md"
                : "border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30"
            } ${!locationData.length ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={() => {
              if (!locationData.length) return;
              setIsSales(!isSales);
            }}
          >
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={isSales}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  setIsSales(e.target.checked);
                }}
                className="w-5 h-5 accent-indigo-600 cursor-pointer"
                disabled={!locationData.length}
              />

              <div>
                <p className="font-semibold text-gray-800">Enable Sales</p>
                <p className="text-xs text-gray-500 mt-1">
                  {isSales
                    ? "Sales data will be included in upload"
                    : "Sales data will not be included in upload"}
                </p>
              </div>
            </div>
          </div>
          {!locationData.length && (
            <p className="text-sm text-amber-600 mt-3 flex items-center gap-2 bg-amber-50 p-3 rounded-lg">
              <span className="text-lg">⚠️</span>
              Please upload a file first to enable Sales check
            </p>
          )}
        </div>

        {/* Market Selection - Only for Single SCM */}
        {scmType === "single" && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow animate-fadeIn">
            <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-4">
              <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-sm">
                {mode === "evaluate" ? "9" : "8"}
              </span>
              Select Markets
              <span className="text-red-500">*</span>
            </label>
            <Select
              mode="multiple"
              placeholder="Choose one or more markets"
              value={selectedMarketsIncludes}
              onChange={(e: any) => setSelectedMarketsIncludes(e)}
              size="large"
              className="w-full"
              // Options come from `result` (summarizeMarkets(csvData) - the
              // raw uploaded CSV's unique locations), NOT from locationData
              // (the geocoded/normalized names from processLocations).
              // includeMarket sent to the backend must match df['location']
              // in prepare_dataframes, which is cleaned straight from the
              // raw CSV - never geocoded - so using raw CSV text here keeps
              // Select Markets, preselection, and job submission all on the
              // same, backend-matching source of truth, and no longer
              // depends on the (slow, sometimes-unreliable) live geocoding
              // call completing before markets can be picked.
              // Selected markets sorted to the top - with 40-50+ markets in
              // this list, scrolling to find which ones are already checked
              // was impractical otherwise.
              options={(result || [])
                .filter((ele: any) => !!ele.location)
                .sort((a: any, b: any) => {
                  const aSelected = selectedMarketsIncludes.includes(a.location);
                  const bSelected = selectedMarketsIncludes.includes(b.location);
                  if (aSelected === bSelected) return 0;
                  return aSelected ? -1 : 1;
                })
                .map((ele: any) => ({
                  label: ele.location,
                  value: ele.location,
                }))}
              maxTagCount="responsive"
              // Explicit checkbox + bold/blue styling per option so a
              // selected market is unmistakable while scrolling a long
              // dropdown list - antd's own default selected-item
              // highlighting wasn't visibly distinct enough here.
              optionRender={(option) => {
                const isSelected = selectedMarketsIncludes.includes(option.value as string);
                return (
                  <div className="flex items-center gap-2">
                    <Checkbox checked={isSelected} />
                    <span className={isSelected ? "font-semibold text-indigo-700" : "text-gray-700"}>
                      {option.label}
                    </span>
                  </div>
                );
              }}
            />
            <p className="text-sm text-gray-500 mt-2">
              Select the markets you want to analyze individually
            </p>
          </div>
        )}

        {/* AI QC Toggle */}
        {["dev", "staging"].includes(process.env.REACT_APP_ENV ?? "dev") && (

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-4">
              <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-sm">
                {(mode === "evaluate") ? "10" : "9"}
              </span>
              AI Quality Check
            </label>

            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div>
                <p className="font-semibold text-gray-800">
                  Enable AI QC Computation
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Run AI-based quality checks on input data
                </p>
              </div>

              <Checkbox
                checked={isAiQcEnabled}
                onChange={(e) => setIsAiQcEnabled(e.target.checked)}
                className="scale-125"
              />
            </div>

            {isAiQcEnabled && (
              <p className="text-sm text-blue-600 mt-3 flex items-center gap-2 bg-blue-50 p-3 rounded-lg">
                <span>ℹ️</span>
                AI QC is enabled.
              </p>
            )}
          </div>
        )}


        {/* Loading Indicator for Geocoding */}
        {loadingLocations && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 border-2 border-blue-200 shadow-lg">
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <CustomExcelIcon className="text-blue-600 text-2xl" />
                </div>
              </div>
              <div className="text-center">
                <p className="font-semibold text-blue-900 text-lg">
                  Processing Locations
                </p>
                <p className="text-blue-600 text-sm mt-1">
                  Fetching coordinates and preparing map data...
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Map Visualization */}
        {(cityDataForMap.length > 0 || US_STATE_DATA.length > 0) &&
          !loadingLocations && (
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <span className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white text-lg">
                    📍
                  </span>
                  Geographic Distribution
                </h3>
                <p className="text-sm text-gray-500 mt-2">
                  Visual representation of your campaign markets and sales data
                </p>
              </div>
              <div
                className="w-full overflow-visible rounded-lg"
                style={{ touchAction: "auto" }}
              >
                <WorldUSSalesMap
                  stateData={US_STATE_DATA}
                  showLegend={true}
                  cityData={cityDataForMap}
                />
              </div>
            </div>
          )}

        {/* Database Data Selector Modal */}
        <DatabaseDataSelector
          isOpen={showDatabaseModal}
          onClose={() => setShowDatabaseModal(false)}
          onConfirm={handleLoadFromDatabase}
          isLoading={loadingDatabase}
        />

        {/* Column Mapping Modal */}
        {validationResult && (
          <ColumnMappingModal
            visible={showMappingModal}
            validationResult={validationResult}
            title="Map Your Data Columns"
            onConfirm={handleMappingConfirm}
            onCancel={handleMappingCancel}
            confirmText="Upload & Continue"
          />
        )}

        {/* Data Preview & Validation Modal - runs full-file validation and
            shows start/middle/end sample rows before anything is uploaded */}
        <DataPreviewValidationModal
          visible={showPreviewModal}
          loading={validatingData}
          fileName={pendingFile?.name}
          validationResponse={dataValidationResponse}
          onConfirm={handlePreviewConfirm}
          onCancel={handlePreviewCancel}
        />

        {/* <style jsx>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out;
          }
        `}</style> */}
      </div>
    );
  }
);

export default PreCampaign;
